import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CellClickEvent, GridComponent, SelectableMode, SelectableSettings } from '@progress/kendo-angular-grid';
import { Subscription } from 'rxjs';
import { RaciNavigationService } from '../../../../navigation/raci-navigation.service';
import { RaciService } from '../../../raci/services/raci.service';
import { GroupApprovalData } from '../../../raci/models/group-approval-data';
import { ActionStageEnum } from 'src/app/modules/raci/models/enums/action-stage-enum';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ArgusSyncOptionEnum } from '../../../../models/enums/argus-sync-option.enum';
import { AppStorageService } from '../../../../services/app-storage.service';
import { EventBusService } from '../../../../services/event-bus.service';
import { ArgusIntegrationService } from '../../../../services/ArgusIntegrationService';

/**
 * Push to Argus matrix
 */
@Component({
  selector: 'argus-push-matrix',
  templateUrl: './argus-push-matrix.component.html',
  styleUrls: ['./argus-push-matrix.component.css']
})
export class ArgusPushMatrixComponent implements OnInit, OnDestroy {
  /**
   * Is sync allowed
   */
  @Input() allowSync: boolean;
  /**
   * Sets active task
   */
  @Input() set currentTask(data: any) {
    if (data) {
      this.activeTask = data;
    }
  }
  /**
   * Gets active task
   */
  activeTask: any;
  /**
   * Allows to show/hide the title info
   */
  @Input() showTitleInfo = false;
  /**
   * Holds the group by value
   */
  @Input() groupBy: string;
  /**
   * Holds the active portfolio id
   */
  @Input() activePortfolioId: any;
  /**
   * Holds the active task id
   */
  @Input() activeTaskId: any;
  /**
   * Holds the active step id
   */
  @Input() activeStepId: any;
  /**
   * Holds the active project id
   */
  @Input() activeProjectId: any;
  /**
   * Holds the current user
   */
  @Input() currentUser;
  /**
   * Holds last sync info
   */
  @Input() lastSyncInfo: any;
  /**
   * Holds the number of properties to sync
   */
  @Input() numberOfSyncProp: number;
  /**
   * Sets is property selected
   */
  @Input() set data(value: any) {
    if (value) {
      this.resetSelections();
      this.properties = value;
      this.properties.forEach(prop => {
        prop.isSelected = false;
      });
    }
  }
  /**
   * On update approval matrix
   */
  @Output() onUpdateApprovalMatrix: EventEmitter<any[]> = new EventEmitter<any[]>();
  /**
   * Update complete task status
   */
  @Output() updateCompleteTaskStatus: EventEmitter<boolean> = new EventEmitter<boolean>();
  /**
   * Should show dialog
   */
  @Output() shouldShowDialog: EventEmitter<boolean> = new EventEmitter<boolean>();
  /**
   * Refresh user task list
   */
  @Output() refreshUserTaskList: EventEmitter<boolean> = new EventEmitter<boolean>();
  /**
   * Update active task
   */
  @Output() updateActiveTask: EventEmitter<any> = new EventEmitter<any>();
  /**
   * Array for dirty properties
   */
  dirtyProperties: Array<any> = new Array<any>();
  /**
   * Properties array
   */
  properties: any[];
  /**
   * Current grid reference
   */
  currentGridRef: GridComponent;
  /**
   * Is project approval selected
   */
  isProjectApprovalSelected: boolean;
  /**
   * Selected group
   */
  selectedGroup: string;
  /**
   * Project
   */
  project: any;
  /**
   * Valuation reports
   */
  valuationReports: any;
  /**
   * Selected report id
   */
  selectedReportId: any;
  /**
   * Bool for is project selected
   */
  isProjectSelected = false;
  /**
   * Selectable settings
   */
  selectableSettings: SelectableSettings;
  /**
   * Mode
   */
  mode: SelectableMode = "single";
  /**
   * Drag
   */
  drag = false;
  /**
   * Is approval dialog displayed
   */
  isApprovalDialogDisplayed = false;
  /**
   * Is approve triggered
   */
  isApproveTriggered = false;
  /**
   * Properties by sector
   */
  propertiesBySector: Array<GroupApprovalData> = [];
  /**
   * Properties by region
   */
  propertiesByRegion: Array<GroupApprovalData> = [];
  /**
   * Group data
   */
  groupData: any;
  /**
   * Project id
   */
  projectId: number;
  /**
   * Subscriptions
   */
  private _subscriptions: Array<Subscription> = new Array<Subscription>();
  /**
  * Sync Options Form
  */
  syncOptionsForm: UntypedFormGroup;

  /**
   * Active Portfolio Source Valuation Event Id
   */
  activePortfolioSourceValuationEventId: any;

  /**
   * Should display create argus portfolio
   */
  shouldDisplayCreateArgusPortfolio: any;

  /**
   * Constructor
   * @param _raciNavigationService
   * @param appStorage
   * @param _raciService
   * @param _fb
   * @param _eventBus
   * @param _argusIntegrationService
   */
  constructor(private _raciNavigationService: RaciNavigationService,
    private appStorage: AppStorageService,
    private _raciService: RaciService,
    private _fb: UntypedFormBuilder,
    private _eventBus: EventBusService,
    private _argusIntegrationService: ArgusIntegrationService) {
    this.setSelectableSettings();
  }

  /**
   * OnInit
   */
  ngOnInit(): void {
    const portfolio = this.appStorage.getSelectedPortfolio();
    this.projectId = portfolio?.ProjectID;
    this.subscribeToSyncProeprtyEvent();
    this.activePortfolioId = this.activeTask?.taskValuationPortfolioId ?? this.activeTask.portfolioId;
    this.activeProjectId = this.projectId ?? this.activeTask.projectId;
    this.getReports();
    this.setLastSyncInfo();
    this.syncOptionsForm = this._fb.group({ option: '' });

    this.activePortfolioSourceValuationEventId = this._raciNavigationService.getActivePortfolio()?.SourceValuationEventId;
    this.shouldDisplayCreateArgusPortfolio = this._raciNavigationService.getActivePortfolio()?.ArgusPortfolioId !== null && this._raciNavigationService.getActivePortfolio()?.ArgusPortfolioId !== 0;

    this._subscriptions.push(this._raciNavigationService._openReport.subscribe(data => {
      if (data) {
        this.createReportLink();
      }
    }));
  }

  /**
  * Action stage enum getter
  */
  get ActionStageEnum() {
    return ActionStageEnum;
  }

  /**
  * Retrieves argus sync option enum
  */
  get ArgusSyncOptionEnum() {
    return ArgusSyncOptionEnum;
  }

  /**
   * Sets last argus sync info
   */
  private setLastSyncInfo(){
    this.numberOfSyncProp = this.properties.filter(p => p.dataStatus !== 'NOT SYNCED').length;
    if (this.numberOfSyncProp === 0) {
      this.lastSyncInfo = [...this.properties].sort(property => property.lastDataSync)[this.properties.length - 1];//Don't sort the original array
    } else {
      this.lastSyncInfo = this.properties.filter(property => property.dataStatus !== 'NOT SYNCED').sort(property => property.lastDataSync)[this.numberOfSyncProp - 1];
    }
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
    this._raciNavigationService.onSyncPortfolioWithArgus(false, 0);
  }

  /**
* On changes actions
*/
  ngOnChanges() {
    if (this.properties) {
      this.groupProperties();
    }
  }

  /**
  * Set Selectable Settings
  */
  public setSelectableSettings(): void {
    this.selectableSettings = {
      mode: this.mode,
      drag: this.drag,
    };
  }

  /**
   * Create report link
   */
  createReportLink() {
    if (this.selectedReportId) {
      const absUrl = window.location.href;
      let url = absUrl.split("#")[0];
      url += "#/rd";
      url += "?ViewReportId=" + this.selectedReportId;
      window.open(url, '_blank');
    }
  }

  /**
   * Get Reports of valuation event
   */
  getReports() {
    this._subscriptions.push(this._raciService.getReports(this.activeProjectId, this.activePortfolioId).subscribe(data => {
      if (data) {
        this.valuationReports = data;
      }
    }));
  }

  /**
   *  Retieves all grid rows
   */
  public getAllRows(): Array<HTMLTableElement> {
    return Array.from(document.querySelectorAll(".properties tr"));
  }

  /**
* Selection change event for grouped data
* @param selectedGroup Selected group
*/
  onSelectionChange(selectedGroup: string) {
    this.groupData.forEach(group => {
      if (group.group != selectedGroup) {
        group.selectedRows = [];
      }
    });
  }

  /**
 * On approval selection event
 * @param cell Selected kendo grid cell
 */
  public onRowSelection(cell: CellClickEvent) {
    this.getSelectedReport(cell);
  }

  /**
  * On approval selection event
  * @param cell Selected kendo grid cell
  */
  public onCellSelection(cell: CellClickEvent) {
    this.getSelectedReport(cell);
  }

  /**
   * Get selected report
   */
  getSelectedReport(cell: any) {
    const report = this.valuationReports?.find(report => { return report.PropertyId == cell.dataItem.propertyId })
    if (report) {
      this._raciNavigationService.onSelectRow(true);
      this.selectedReportId = report.Oid;
    }
  }

  /**
   * Resets grid selections
   */
  private resetSelections() {
    this.isProjectApprovalSelected = false;
    this.dirtyProperties = [];
    this.isProjectSelected = false;
  }

  /**
   * Subscribes to save approval event
   */
  private subscribeToSyncProeprtyEvent() {
    this._subscriptions.push(this._raciNavigationService._onSaveApproval.subscribe(data => {
      if (data) {
        this.dirtyProperties = [];
        this.updateActiveTask.emit(this.activeTask);
      }
    }));
  }

  /**
   * Select all properies
   */
  selectAllProperties() {
    this.isProjectSelected = true;
    const display = this.isProjectSelected && (this.activeTask.taskActionStage === ActionStageEnum.Waiting || !this.activeTask.taskActionStage);
    this._raciNavigationService.onSyncPortfolioWithArgus(display, this.syncOptionsForm.controls.option.value);
  }

  /**
   * Should display sellect all checkbox
   */
  sholudDisplaySellectAll() {
    return true;
  }

  /**
  * Checks for the most commpon approver
  * @param approvalList Approvals list
  * @param approvalIndex Approval index (eg. 1,2,3)
  * @param approvalA First approval for comparison
  * @param approvalB Second approval for comparison
  */
  private getMostCommonApprover(approvalList: any[], approvalIndex: number, approvalA: any, approvalB: any) {
    return approvalList.filter(v => v[`approval${approvalIndex}`] === approvalB[`approval${approvalIndex}`]).length - approvalList.filter(v => v[`approval${approvalIndex}`] === approvalA[`approval${approvalIndex}`]).length;
  }

  /**
   * Sets most common approvers for specified group
   * @param group Group object
   */
  private setMostCommonApprover(group: any) {
    for (let approvalIndex = 1; approvalIndex <= 3; approvalIndex++) {
      const mostCommonApprover = group.tasks.sort((approvalA, approvalB) => this.getMostCommonApprover(group.tasks, approvalIndex, approvalA, approvalB))[0];
      const pendingApproval = group.tasks.find(x => !x[`dateApproved${approvalIndex}`] && !x[`dateRejected${approvalIndex}`]);
      group.approvals[0][`approval${approvalIndex}`] = mostCommonApprover[`approval${approvalIndex}`];
      group.approvals[0][`approvalUser${approvalIndex}`] = mostCommonApprover[`approvalUser${approvalIndex}`];
      group.approvals[0][`dateRejected${approvalIndex}`] = !mostCommonApprover[`dateRejected${approvalIndex}`] || pendingApproval ? null : new Date(mostCommonApprover[`dateRejected${approvalIndex}`]);
      group.approvals[0][`dateApproved${approvalIndex}`] = !mostCommonApprover[`dateApproved${approvalIndex}`] || pendingApproval ? null : new Date(mostCommonApprover[`dateApproved${approvalIndex}`]);
    }
  }
  /**
   * Getting and parsing the logs from argus for the last sync
   * @param dataItem
   */
  getLastSyncLogs(dataItem) {
    let contentDialog = '';

    this._argusIntegrationService.getArgusLogs(this.activePortfolioId, dataItem.propertyId).subscribe(logs => {
      logs.forEach(logsContent => {
        contentDialog += logsContent.Message + ' <br>';
      });

      this._eventBus.broadcastEvent("argusDialog", {
        success: () => {
          // Nothing
        },
        cancel: () => {
          // Nothing
        },
        dialogContent: () => {
          return contentDialog === "" ? "No available logs." : contentDialog;
        },
        shouldShowCloseButton: () => {
          return true;
        }
      }, 'exportToArgusConfirmDialog');
    });
  }

  /**
 * Check if the group list contains a gorup without name and sets it name to "Unknown"
 * @param groupList Group list
 */
  private checkForUnknownGroup(groupList: any[]) {
    const unknownSector = groupList.find(item => { return !item.group });
    if (unknownSector) {
      unknownSector.group = "Unknown";
    }
  }

  /**
   * Groups properties by sector
   */
  private groupBySector() {
    this.propertiesBySector = [];
    this.properties.forEach(property => {
      if (!this.propertiesBySector.find(item => { return item.group == property.sector })) {
        this.propertiesBySector.push(new GroupApprovalData({ "group": property.sector }));
      }
      this.propertiesBySector.find(item => { return item.group == property.sector }).tasks.push(property);
    });

    this.propertiesBySector.forEach(sector => {
      this.setMostCommonApprover(sector);
    });

    this.checkForUnknownGroup(this.propertiesBySector);
  }

  /**
 * Groups properties by region
 */
  private groupByRegion() {
    this.propertiesByRegion = [];
    this.properties.forEach(property => {
      if (!this.propertiesByRegion.find(item => { return item.group == property.region })) {
        this.propertiesByRegion.push(new GroupApprovalData({ "group": property.region }));
      }
      this.propertiesByRegion.find(item => { return item.group == property.region }).tasks.push(property);
    });

    this.propertiesByRegion.forEach(region => {
      this.setMostCommonApprover(region);
    });

    this.checkForUnknownGroup(this.propertiesByRegion);
  }

  /**
 * Group proerties by sector or region.
 */
  private groupProperties() {
    this.groupBySector();
    this.groupByRegion();
    this.groupData = this.groupBy == 'Sector' ? this.propertiesBySector : this.propertiesByRegion;
    this._raciNavigationService.onSelectRow(false);
  }
}
